import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { useCreateNote } from "../hooks";

interface NoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string | undefined;
  source: string;
  title?: string;
}

export function NoteDialog({
  isOpen,
  onClose,
  itemId,
  source,
  title,
}: NoteDialogProps) {
  const [isInternal, setIsInternal] = useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>("");

  // Use the send note hook
  const { mutateAsync: createNote, isPending: isCreating } = useCreateNote();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsInternal(false);
      setMessageText("");
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!messageText.trim() || !itemId) return;

    try {
      // Prepare the note data using the correct CreateNoteInput structure
      const noteData = {
        entity_type: source,
        entity_id: itemId,
        remoteUtilityId: getRemoteUtilityId(),
        note_content: messageText.trim(),
      };

      createNote(noteData);

      onClose();
    } catch (error) {
      toast({
        title: `Failed to add note: ${error.message || "Unknown error"}`,
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Add Note
          </DialogTitle>
          <DialogDescription>Add a note for selected {title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-600">
              Note will be added to the {title}'s record
            </p>
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <div className="flex gap-2 mt-2">
              <Textarea
                id="message"
                placeholder="Type your message here..."
                className="resize-none flex-1"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                disabled={isCreating}
                rows={4}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!messageText.trim() || isCreating}
              className="flex items-center gap-2"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isCreating ? "Adding..." : "Add Note"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
